import React, { useState, useEffect } from 'react';
import { app, database, storage } from '../../../config/firebase';
import { ref as dbRef, onValue, remove } from 'firebase/database';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { AiOutlineEye, AiOutlineDelete, AiOutlineBulb, AiOutlineShareAlt } from 'react-icons/ai';
import Header from "../../../components/protected/Header";
import { useTranslations } from '../../../translations';
import { UserAuth } from '../../../context/AuthContext';
import { useSchoolRole } from '../../../hooks/useSchoolRole';
import { distributeToSection } from '../../../components/utils/distributeSection';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
});

const InferenceList = () => {
  const { t } = useTranslations();
  const { user } = UserAuth();
  const { role } = useSchoolRole();
  const isTeacher = role === "maestro";
  const navigate = useNavigate();
  const [savedLessons, setSavedLessons] = useState([]);

  useEffect(() => {
    if (!user || !user.uid) return;
    const lessonsRef = dbRef(database, `users/${user.uid}/inference_lessons`);
    const unsubscribe = onValue(lessonsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const lessonsArray = Object.entries(data).map(([id, val]) => ({
          id,
          ...val
        }));
        setSavedLessons(lessonsArray.sort((a, b) => b.createdAt - a.createdAt));
      } else {
        setSavedLessons([]);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const handleDelete = async (lessonId) => {
    try {
      const result = await Swal.fire({
        title: t("deleteConfirmationTitle") || '¿Estás seguro?',
        text: t("deleteConfirmationText") || 'Esta acción no se puede deshacer.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t("delete") || 'Sí, eliminar',
        cancelButtonText: t("cancel") || 'Cancelar'
      });

      if (result.isConfirmed) {
        await remove(dbRef(database, `users/${user.uid}/inference_lessons/${lessonId}`));
        Toast.fire({
          icon: 'success',
          title: t("deletedSuccessfully") || 'Lección eliminada'
        });
      }
    } catch (error) {
      console.error("Error al eliminar:", error);
      Toast.fire({ icon: 'error', title: t("errorDeleting") });
    }
  };

  const handleViewLesson = (lesson) => {
    // Navigate to the view/read-only page passing the lesson as state
    navigate('/inference/view', { state: { lesson } });
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="dashboard-title animate__animated animate__fadeInDown text-plum fs-2 fw-bold d-flex align-items-center gap-3">
               <AiOutlineBulb /> {t("savedLessonsTitle") || "Contenidos Guardados"}
            </h1>
            <Button className="btn-primary-custom px-4 fw-bold" onClick={() => navigate('/inference/create')}>
              + {t("generateInference") || "Generar Nuevo"}
            </Button>
          </div>

          {!savedLessons || savedLessons.length === 0 ? (
            <div className="text-center mt-5 p-5 bg-light rounded-4 border">
              <span className="fs-5 text-muted">{t("noSavedLessons")}</span>
            </div>
          ) : (
            <Row className="g-4 animate__animated animate__fadeInUp">
              {savedLessons.map((lesson) => (
                <Col lg={4} md={6} key={lesson.id}>
                  <Card className="modern-card h-100 shadow-sm border-0" style={{ borderRadius: '15px', overflow: 'hidden' }}>
                    <div className="position-relative" style={{ height: '220px', backgroundColor: '#f8f9fa' }}>
                      {lesson.imageUrl && (
                        <img 
                          src={lesson.imageUrl} 
                          alt={lesson.idea} 
                          className="w-100 h-100" 
                          style={{ objectFit: 'cover' }} 
                        />
                      )}
                      <div className="position-absolute bottom-0 w-100 p-3" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
                        <h5 className="text-white fw-bold mb-0 text-truncate" title={lesson.idea}>
                          {lesson.idea}
                        </h5>
                      </div>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <div className="text-muted small mb-3 flex-grow-1" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {lesson.readingText}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                        <span className="badge bg-light text-dark border border-secondary font-monospace">
                          {new Date(lesson.createdAt).toLocaleDateString()}
                        </span>
                        <div className="d-flex gap-2">
                          {isTeacher && (
                            <Button
                              size="sm"
                              className="px-3 py-1 d-flex align-items-center justify-content-center"
                              style={{ borderRadius: '8px', background: '#eb2178', border: 'none' }}
                              onClick={() => distributeToSection(lesson, "inference", t)}
                              title={t("distributeTooltip")}
                            >
                              <AiOutlineShareAlt size={18} />
                            </Button>
                          )}
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="px-3 py-1 d-flex align-items-center justify-content-center"
                            style={{ borderRadius: '8px' }}
                            onClick={() => handleViewLesson(lesson)}
                            title={t("view") || "Ver"}
                          >
                            <AiOutlineEye size={18} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            className="px-3 py-1 d-flex align-items-center justify-content-center"
                            style={{ borderRadius: '8px' }}
                            onClick={() => handleDelete(lesson.id)}
                            title={t("delete") || "Eliminar"}
                          >
                            <AiOutlineDelete size={18} />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </main>
    </div>
  );
};

export default InferenceList;
